import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import {
  alpha,
  Box,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { SimilarSupplierCandidate } from '../../services/suppliers/similar-suppliers';
import { MODULE_ACCENT_COLORS } from '../../theme/module-colors';
import { FormDialog } from './form-dialog';

const ACCENT = MODULE_ACCENT_COLORS.suppliers.main;

const CREATE_ANYWAY_VALUE = 'create-anyway';

interface SimilarSupplierDialogProps {
  open: boolean;
  attemptedName: string;
  candidates: SimilarSupplierCandidate[];
  onClose: () => void;
  onUseExisting: (candidate: SimilarSupplierCandidate) => void;
  onCreateAnyway: () => void;
  isSubmitting?: boolean;
}

interface OptionCardProps {
  value: string;
  selected: boolean;
  icon: ReactNode;
  label: string;
  badge?: ReactNode;
  helper?: string;
}

function OptionCard({ value, selected, icon, label, badge, helper }: OptionCardProps) {
  return (
    <FormControlLabel
      value={value}
      control={<Radio sx={{ alignSelf: 'flex-start', mt: 0.5 }} />}
      sx={{
        m: 0,
        width: '100%',
        alignItems: 'flex-start',
        gap: 1,
        p: 1.5,
        minHeight: 48,
        borderRadius: 2,
        border: '1px solid',
        borderColor: selected ? ACCENT : 'divider',
        backgroundColor: selected ? alpha(ACCENT, 0.06) : 'transparent',
        transition: 'background-color 0.2s, border-color 0.2s',
      }}
      label={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              aria-hidden="true"
              sx={{ display: 'flex', color: selected ? ACCENT : 'text.secondary' }}
            >
              {icon}
            </Box>
            <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
            {badge}
          </Box>
          {helper && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {helper}
            </Typography>
          )}
        </Box>
      }
    />
  );
}

interface FormValues {
  selection: string;
}

export function SimilarSupplierDialog({
  open,
  attemptedName,
  candidates,
  onClose,
  onUseExisting,
  onCreateAnyway,
  isSubmitting = false,
}: SimilarSupplierDialogProps) {
  const { t } = useTranslation('suppliers');

  const { control, handleSubmit, watch, reset } = useForm<FormValues>({
    defaultValues: { selection: '' },
  });
  const selection = watch('selection');
  const isValid = selection !== '';

  function onSubmit(values: FormValues) {
    if (values.selection === CREATE_ANYWAY_VALUE) {
      onCreateAnyway();
      return;
    }
    const candidate = candidates.find((c) => c.id === values.selection);
    if (!candidate) {
      return;
    }
    onUseExisting(candidate);
  }

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('similarSupplierTitle')}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isValid={isValid}
      submitLabel={t('similarSupplierConfirmLabel')}
      onTransitionExited={() => reset({ selection: '' })}
    >
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('similarSupplierIntro', { name: attemptedName })}
      </Typography>

      <Controller
        name="selection"
        control={control}
        render={({ field }) => (
          <RadioGroup {...field}>
            <Stack spacing={1}>
              {candidates.map((candidate) => (
                <OptionCard
                  key={candidate.id}
                  value={candidate.id}
                  selected={field.value === candidate.id}
                  icon={<StorefrontOutlinedIcon fontSize="small" />}
                  label={candidate.name}
                  badge={
                    !candidate.isActive && (
                      <Chip size="small" color="default" label={t('similarSupplierInactive')} />
                    )
                  }
                  helper={`${t('vatNumber')} ${candidate.vatNumber}`}
                />
              ))}
              <OptionCard
                value={CREATE_ANYWAY_VALUE}
                selected={field.value === CREATE_ANYWAY_VALUE}
                icon={<AddBusinessOutlinedIcon fontSize="small" />}
                label={t('similarSupplierCreateAnyway')}
              />
            </Stack>
          </RadioGroup>
        )}
      />
    </FormDialog>
  );
}
